import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useMemo, useState } from 'react';
import { ChatProvider } from '@/ui/ChatProvider';
import { StandaloneChat } from '@/ui/StandaloneChat';
import { ChatWidget } from '@/ui/ChatWidget';
import { MockTransport } from '@/transport/mock/mockTransport';
import { AgentApiTransport } from '@/transport/api/agentApiTransport';
import { applyTheme } from '@/core/theme';
import { resolveConfig } from '@/core/config';
import { DevPanel } from './DevPanel';
const env = import.meta.env;
// Credentials are added by the dev proxy / nginx, so the bundle can't tell
// whether a backend is configured. Default to it; the dev panel switches to
// the mock when working offline.
const DEFAULTS = {
    backend: 'agent-api',
    presentation: 'fullscreen',
    streaming: true,
    colorScheme: 'auto',
    primary: '#d97757',
    errorRate: 0,
    home: true,
};
/** The sales widget's Messages and Home screens; the playground's ⚙ turns them off. */
const SAMPLE_HOME = {
    headline: ['Hello there.', 'How can we help?'],
    cards: [
        { type: 'ask', title: 'Ask a question', body: 'CAIRA, our AI assistant, replies instantly' },
        {
            type: 'link', size: 'large', icon: 'erp', color: '#141413',
            label: 'Enterprise ERP', title: 'Align360',
            body: 'From accounting to payroll, CRM to HRMS: everything in one powerful, true cloud ERP.',
            cta: 'Explore Align360', url: 'https://alignbooks.com/enterprise',
        },
        {
            type: 'link', size: 'large', icon: 'cart',
            label: 'B2C app', title: 'AlignCart',
            body: 'Sell your products online, on your own app.',
            cta: 'See AlignCart', url: 'https://alignbooks.com/aligncart',
        },
        {
            type: 'prompt', size: 'large', icon: 'cloud', color: '#2f5d8a',
            label: 'Cloud & security', title: 'Hosted on AWS cloud',
            body: 'Full data protection, with automatic backups.',
            cta: 'Ask how your data is protected', message: 'How is AlignBooks hosted, and how is my data protected and backed up?',
        },
        {
            type: 'prompt', size: 'large', icon: 'migrate', color: '#4a7a6b',
            label: 'Easy migration', title: 'Move to AI-native AlignBooks',
            body: 'Switching from Tally or another system? Your data comes with you.',
            cta: 'Ask how migration works', message: 'How do I migrate to AlignBooks from my current software?',
        },
    ],
};
/**
 * Development shell. Not part of the shipped widget — it exists so both
 * presentations and both transport modes can be exercised side by side.
 */
export function App() {
    const [settings, setSettings] = useState(DEFAULTS);
    const config = useMemo(() => ({
        presentation: settings.presentation,
        transport: settings.backend === 'agent-api'
            ? new AgentApiTransport({
                baseUrl: env.VITE_API_BASE_URL ?? '',
                widgetKey: env.VITE_WIDGET_KEY || undefined,
                bootstrapMessage: env.VITE_BOOTSTRAP_MESSAGE || undefined,
            })
            : new MockTransport({
                streaming: settings.streaming,
                errorRate: settings.errorRate,
            }),
        theme: { primary: settings.primary, colorScheme: settings.colorScheme },
        home: settings.home ? SAMPLE_HOME : undefined,
        // Branding and the local welcome come from resolveConfig's defaults.
        context: { page: typeof location !== 'undefined' ? location.pathname : '/' },
    }), [settings]);
    // Remounting on settings change is intentional here: it proves a cold start
    // works for every combination.
    const key = JSON.stringify(settings);
    const rootRef = (node) => {
        if (node)
            applyTheme(node, resolveConfig(config).theme);
    };
    return (_jsxs("div", { className: "ac-root", ref: rootRef, style: { height: '100dvh' }, children: [_jsx(DevPanel, { settings: settings, onChange: setSettings }), _jsx(ChatProvider, { config: config, children: settings.presentation === 'fullscreen' ? _jsx(StandaloneChat, {}) : _jsx(ChatWidget, {}) }, key)] }));
}
